import { Box, Typography } from '@mui/material'
import { useOnboarding } from '../context/OnboardingContext'
import type { StepId } from '../types/onboarding'
import {
  ALL_PROGRESS_BAR_PHASES,
  percentCompleteForStep,
  phaseIndexForStep,
  showProgressBarForStep,
  stepIdForPhase,
} from './onboardingPhases'
import { MISC_PRIMARY } from '../theme/onboardingTheme'

const TRACK_COLOR = '#3d6b7a'
/** Center of first / last dot as % of row width (equal grid columns). */
function trackInset(phaseCount: number) {
  return `${50 / phaseCount}%`
}

export function OnboardingPhaseProgress({ currentStepId }: { currentStepId: StepId }) {
  const { goToStep } = useOnboarding()

  const phases = ALL_PROGRESS_BAR_PHASES
  const phaseCount = phases.length
  const trackInsetPct = trackInset(phaseCount)

  if (!showProgressBarForStep(currentStepId)) {
    return null
  }

  const activePhaseIndex = phaseIndexForStep(currentStepId, phases)
  const percent = percentCompleteForStep(currentStepId, phases)

  return (
    <Box sx={{ mt: 3, width: '100%' }}>
      <Typography sx={{ textAlign: 'center', fontSize: 18, fontWeight: 700, letterSpacing: '-0.025em', color: '#0f172a' }}>
        {percent}% Complete
      </Typography>

      <Box
        role="list"
        aria-label="Onboarding progress"
        sx={{
          position: 'relative',
          mt: 4,
          display: 'grid',
          width: '100%',
          gridTemplateColumns: `repeat(${phaseCount}, minmax(0, 1fr))`,
        }}
      >
        <Box
          aria-hidden
          sx={{
            pointerEvents: 'none',
            position: 'absolute',
            top: 8,
            zIndex: 0,
            height: 8,
            overflow: 'hidden',
            borderRadius: 999,
            backgroundColor: '#e2e8f0',
            left: trackInsetPct,
            right: trackInsetPct,
          }}
        >
          <Box
            sx={{
              height: '100%',
              borderRadius: 999,
              width: `${percent}%`,
              backgroundColor: TRACK_COLOR,
              transition: 'width 500ms ease-out',
            }}
          />
        </Box>

        {phases.map((phase, index) => {
          const isActive = activePhaseIndex === index
          const targetStepId = stepIdForPhase(phase)

          return (
            <Box
              key={phase.id}
              role="listitem"
              sx={{ position: 'relative', zIndex: 10, display: 'flex', justifyContent: 'center', px: 0.25 }}
            >
              <Box
                component="button"
                type="button"
                onClick={() => goToStep(targetStepId)}
                aria-current={isActive ? 'step' : undefined}
                aria-label={`Go to ${phase.label}`}
                sx={{
                  display: 'flex',
                  cursor: 'pointer',
                  flexDirection: 'column',
                  alignItems: 'center',
                  border: '1px solid transparent',
                  borderRadius: 1,
                  backgroundColor: 'transparent',
                  px: 0.5,
                  pb: 0.5,
                  pt: 0.25,
                  '&:hover': {
                    borderColor: '#e2e8f0',
                    backgroundColor: '#f8fafc',
                  },
                  '&:focus-visible': {
                    outline: 'none',
                    borderColor: 'rgba(37, 99, 235, 0.3)',
                    boxShadow: `0 0 0 2px rgba(37, 99, 235, 0.4)`,
                  },
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    height: 16,
                    width: 16,
                    flexShrink: 0,
                    borderRadius: '50%',
                    backgroundColor: TRACK_COLOR,
                    boxShadow: '0 0 0 4px #ffffff',
                    transform: isActive ? 'scale(1.1)' : 'none',
                    transition: 'transform 150ms',
                    '&:hover': { transform: 'scale(1.1)' },
                  }}
                />
                <Typography
                  sx={{
                    mt: 1.5,
                    maxWidth: { xs: 112, sm: 120 },
                    textAlign: 'center',
                    fontSize: { xs: 10, sm: 11 },
                    fontWeight: 600,
                    lineHeight: 1.25,
                    color: isActive ? '#0f172a' : '#475569',
                    '&:hover': { color: isActive ? '#0f172a' : MISC_PRIMARY },
                  }}
                >
                  {phase.label}
                </Typography>
              </Box>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
